/* @flow */

import clsx from 'clsx';
import { produce } from 'immer';
import * as React from 'react';
import { connect } from 'react-redux';

import type { SCHEDULED_RECORDING_CORE_SETTINGS } from '../../../helpers/npvr/Types';
import type { ModalState } from '../../../redux/modal/reducers';
import type { CombinedReducers } from '../../../redux/reducers';

import Localizer from '../../../helpers/localization';
import { Setting } from '../../../helpers/settings/types';
import { ScheduledRecordingsKind, type ScheduledRecordingsKindType } from '../../../libs/netgemLibrary/v8/types/Npvr';
import ButtonFX from '../../buttons/ButtonFX';
import { WidthKind } from '../../buttons/types';
import MultipleChoices, { LabelWidth } from '../../multipleChoices/MultipleChoices';
import { PictoArrowLeft } from '../../pictos/Element';
import Modal, { ModalView } from '../modal';
import './NpvrModal.css';

export enum NpvrModalDialogResult {
  Cancel,
  DeleteEpisode,
  RecordEpisode,
  RecordSeries,
  StopSeries,
}

export type NPVR_MODAL_RESULT = {
  dialogResult: NpvrModalDialogResult,
  settings?: SCHEDULED_RECORDING_CORE_SETTINGS,
};

export type NPVR_DATA_MODAL_TYPE = {
  episodeIndex: ?string,
  episodeTitle: ?string,
  isEpisodeDeletable: boolean,
  isEpisodeRecordable: boolean,
  isSeriesRecordable: boolean,
  kind: ?ScheduledRecordingsKindType,
  seriesTitle: ?string,
};

type ReduxNpvrModalReducerStateType = {
  +isDebugModeEnabled: boolean,
};

type NpvrModalStateType = {
  increasingEpisodesIndex: number,
  isSeriesMode: boolean,
  recordsToKeepIndex: number,
  settings?: SCHEDULED_RECORDING_CORE_SETTINGS,
};

const InitialState = Object.freeze({
  increasingEpisodesIndex: -1,
  isSeriesMode: false,
  recordsToKeepIndex: -1,
  settings: undefined,
});

type CompleteConfirmationModalPropType = {
  ...ModalState,
  ...ReduxNpvrModalReducerStateType,
};

class NpvrModalView extends React.PureComponent<CompleteConfirmationModalPropType, NpvrModalStateType> {
  isBackDisplayed: boolean;
  modal: ModalView | null;

  constructor(props: CompleteConfirmationModalPropType) {
    super(props);

    const { isDebugModeEnabled, npvrData } = props;
    const isSeriesMode = Boolean(npvrData && npvrData.kind === ScheduledRecordingsKind.Series);

    this.isBackDisplayed = !isSeriesMode;
    this.modal = null;

    if (isDebugModeEnabled) {
      // Debug mode: new custom series settings
      this.state = {
        ...InitialState,
        increasingEpisodesIndex: 0,
        isSeriesMode,
        recordsToKeepIndex: 3,
        settings: {
          increasingEpisodes: true,
          recordsToKeep: 0,
        },
      };
    } else {
      // Old version: no series settings
      this.state = {
        ...InitialState,
        isSeriesMode,
      };
    }
  }

  close = (result: NPVR_MODAL_RESULT): void => {
    const { modal } = this;

    modal?.close(result);
  };

  handleRecordEpisode = () => {
    this.close({ dialogResult: NpvrModalDialogResult.RecordEpisode });
  };

  handleDeleteEpisode = () => {
    this.close({ dialogResult: NpvrModalDialogResult.DeleteEpisode });
  };

  handleRecordSeries = () => {
    const { isDebugModeEnabled } = this.props;

    if (isDebugModeEnabled) {
      // New series options
      this.setState({ isSeriesMode: true });
    } else {
      this.handleRecordSeriesInternal();
    }
  };

  handleStopSeries = () => {
    this.close({ dialogResult: NpvrModalDialogResult.StopSeries });
  };

  handleIncreasingEpisodesOnSelectionChanged = (value: boolean, setting?: Setting, increasingEpisodesIndex: number) => {
    this.setState(
      produce((draft) => {
        draft.increasingEpisodesIndex = increasingEpisodesIndex;

        if (draft.settings === undefined) {
          draft.settings = {} as SCHEDULED_RECORDING_CORE_SETTINGS;
        }

        draft.settings.increasingEpisodes = value;
      }),
    );
  };

  handleRecordsToKeepOnSelectionChanged = (value: number, setting?: Setting, recordsToKeepIndex: number) => {
    this.setState(
      produce((draft) => {
        draft.recordsToKeepIndex = recordsToKeepIndex;

        if (draft.settings === undefined) {
          draft.settings = {} as SCHEDULED_RECORDING_CORE_SETTINGS;
        }

        draft.settings.recordsToKeep = value;
      }),
    );
  };

  renderHeader = (): React.Node => {
    const { npvrData } = this.props;
    const { isSeriesMode } = this.state;

    if (!npvrData) {
      return null;
    }

    const { episodeIndex, episodeTitle, seriesTitle } = npvrData;
    const parts = [];
    if (episodeIndex) {
      parts.push(episodeIndex);
    }
    if (episodeTitle) {
      parts.push(episodeTitle);
    }
    const episodeTitleElement = (!isSeriesMode || !seriesTitle) && parts.length > 0 ? <div className='episodeTitle'>{parts.join(' : ')}</div> : null;

    return (
      <>
        <div className='seriesTitle'>{seriesTitle}</div>
        {episodeTitleElement}
      </>
    );
  };

  renderButtons = (): React.Node => {
    const { npvrData } = this.props;

    if (!npvrData) {
      return null;
    }

    const { isEpisodeDeletable, isEpisodeRecordable, isSeriesRecordable } = npvrData;

    const episodeRecordBtn = isEpisodeRecordable ? (
      <ButtonFX key='recordEpisode' onClick={this.handleRecordEpisode} widthKind={WidthKind.Stretched}>
        {Localizer.localize('modal.npvr_confirmation.record_episode')}
      </ButtonFX>
    ) : null;

    const episodeDeleteBtn = isEpisodeDeletable ? (
      <ButtonFX key='deleteEpisode' onClick={this.handleDeleteEpisode} widthKind={WidthKind.Stretched}>
        {Localizer.localize('modal.npvr_confirmation.delete_episode')}
      </ButtonFX>
    ) : null;

    const seriesRecordBtn = isSeriesRecordable ? (
      <ButtonFX key='recordSeries' onClick={this.handleRecordSeries} widthKind={WidthKind.Stretched}>
        {Localizer.localize('modal.npvr_confirmation.record_series')}
      </ButtonFX>
    ) : null;

    const seriesStopBtn = !isSeriesRecordable ? (
      <ButtonFX key='stopSeries' onClick={this.handleStopSeries} widthKind={WidthKind.Stretched}>
        {Localizer.localize('modal.npvr_confirmation.stop_series')}
      </ButtonFX>
    ) : null;

    return (
      <>
        {episodeRecordBtn}
        {episodeDeleteBtn}
        {seriesRecordBtn}
        {seriesStopBtn}
      </>
    );
  };

  renderRegularBody: () => React.Node = this.renderButtons;

  handleRecordSeriesInternal = () => {
    const { settings } = this.state;

    this.close({
      dialogResult: NpvrModalDialogResult.RecordSeries,
      settings,
    });
  };

  renderSeriesBody = (): React.Node => {
    const { increasingEpisodesIndex, recordsToKeepIndex, settings } = this.state;

    const increasingEpisodesChoices = Object.freeze([
      {
        text: Localizer.localize('modal.npvr_confirmation.options.recent_seasons'),
        value: true,
      },
      {
        text: Localizer.localize('modal.npvr_confirmation.options.whole_series'),
        value: false,
      },
    ]);

    const recordsToKeepChoices = Object.freeze([
      {
        text: '1',
        value: 1,
      },
      {
        text: '3',
        value: 3,
      },
      {
        text: '5',
        value: 5,
      },
      {
        text: Localizer.localize('modal.npvr_confirmation.options.all'),
        value: 0,
      },
    ]);

    const seriesRecordOptions = settings ? (
      <div className='confirmationOptions'>
        <div className='confirmationOption'>
          <MultipleChoices
            ariaLabel={Localizer.localize('accessibility.modal.npvr_season_choice')}
            choices={increasingEpisodesChoices}
            name='seasons'
            onSelectionChanged={this.handleIncreasingEpisodesOnSelectionChanged}
            selectedIndex={increasingEpisodesIndex}
          />
        </div>
        <div className='confirmationOption'>
          <span className='confirmationLabel'>{Localizer.localize('modal.npvr_confirmation.options.records_to_keep')}</span>
          <MultipleChoices
            ariaLabel={Localizer.localize('accessibility.modal.npvr_episodes_to_keep')}
            choices={recordsToKeepChoices}
            name='episodes'
            onSelectionChanged={this.handleRecordsToKeepOnSelectionChanged}
            requestedLabelWidth={LabelWidth.Small}
            selectedIndex={recordsToKeepIndex}
          />
        </div>
      </div>
    ) : null;

    return (
      <>
        {seriesRecordOptions}
        <ButtonFX key='recordSeries' onClick={this.handleRecordSeriesInternal} widthKind={WidthKind.Stretched}>
          {Localizer.localize('common.actions.confirm')}
        </ButtonFX>
      </>
    );
  };

  handleBackOnClick = () => {
    this.setState({ isSeriesMode: false });
  };

  renderBackPicto = (): React.Node => <PictoArrowLeft aria-label={Localizer.localize('accessibility.common.back')} onClick={this.handleBackOnClick} />;

  renderChildren = (): React.Node => {
    const { isSeriesMode } = this.state;
    const { isBackDisplayed } = this;

    return (
      <>
        {isSeriesMode && isBackDisplayed ? this.renderBackPicto() : null}
        <div className={clsx('confirmationBody', isSeriesMode && 'series')}>{isSeriesMode ? this.renderSeriesBody() : this.renderRegularBody()}</div>
      </>
    );
  };

  setModalRef = (instance: ModalView | null) => {
    this.modal = instance;
  };

  render(): React.Node {
    return (
      <Modal ariaLabel={Localizer.localize('accessibility.modal.npvr')} className='npvrConfirmation' header={this.renderHeader()} innerRef={this.setModalRef}>
        {this.renderChildren()}
      </Modal>
    );
  }
}

const mapStateToProps = (state: CombinedReducers) => {
  return {
    isDebugModeEnabled: state.appConfiguration.isDebugModeEnabled,
  };
};

const NpvrModal: React.ComponentType<ModalState> = connect(mapStateToProps)(NpvrModalView);

export default NpvrModal;
